import { FilteredItemProps } from '@delmonicos/shared-design-system-react/src/components/filtered-item/filtered-item';
import { GetUsersPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/users/models/inputs/get-users-paginated.model';
import { GatewayUserModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/users/models/outputs/gateway-user.model';
import {
  ColumnDef,
  ColumnOrderState,
  VisibilityState,
  createColumnHelper,
} from '@tanstack/react-table';

import { CustomerFilterStateController } from '@app/feature/customer/customer-filter-state/customer-filter-state.controller';
import { RoleFilterStateController } from '@app/feature/role/role-filter-state/role-filter-state.controller';

import { UserListCustomerColumn } from './user-list-customer.column';
import { UserListEmailAddressColumn } from './user-list-email-address.column';
import { UserListFirstNameColumn } from './user-list-first-name.column';
import { UserListLastConnectionColumn } from './user-list-last-connection.column';
import { UserListLastNameColumn } from './user-list-last-name.column';
import { UserListMobileNumberColumn } from './user-list-mobile-number.column';
import { UserListRolesColumn } from './user-list-roles.column';

// /////////////////////////////////////////////////////////////////////////////
// SORTING
// /////////////////////////////////////////////////////////////////////////////

export const USER_LIST_SORT_FIELD: NonNullable<
  GetUsersPaginatedModel['sortField']
> = 'createdAt';

export const USER_LIST_SORT_ORDER: NonNullable<
  GetUsersPaginatedModel['sortOrder']
> = 1;

// /////////////////////////////////////////////////////////////////////////////
// FILTERING
// /////////////////////////////////////////////////////////////////////////////

type ResourceListFilter = Partial<
  Record<
    keyof GetUsersPaginatedModel,
    {
      componentType: React.ComponentType<{
        value: any;
        onRemove: FilteredItemProps['onRemove'];
      }>;
    }
  >
>;

export const USER_LIST_FILTER: ResourceListFilter = {
  customerId: {
    componentType: CustomerFilterStateController,
  },
  roleId: {
    componentType: RoleFilterStateController,
  },
} as const;

export const USER_LIST_FILTER_NAME_LIST = Object.keys(
  USER_LIST_FILTER,
) as Array<keyof typeof USER_LIST_FILTER>;

// /////////////////////////////////////////////////////////////////////////////
// DISPLAYING
// /////////////////////////////////////////////////////////////////////////////

export const userListColumnHelper = createColumnHelper<GatewayUserModel>();

type ResourceColumnType = ColumnDef<GatewayUserModel, any>[];

export const USER_LIST_COLUMN_LIST: ResourceColumnType = [
  userListColumnHelper.accessor('firstName', UserListFirstNameColumn),
  userListColumnHelper.accessor('lastName', UserListLastNameColumn),
  userListColumnHelper.accessor('mobileNumber', UserListMobileNumberColumn),
  userListColumnHelper.accessor('emailAddress', UserListEmailAddressColumn),
  userListColumnHelper.accessor('customer', UserListCustomerColumn),
  userListColumnHelper.accessor('roles', UserListRolesColumn),
  userListColumnHelper.accessor('lastConnection', UserListLastConnectionColumn),
];

export const USER_LIST_COLUMN_ID_LIST = USER_LIST_COLUMN_LIST.flatMap(
  (columnDef) => {
    if ('accessorKey' in columnDef) {
      return [columnDef.id ?? (columnDef.accessorKey as string)];
    }

    if (columnDef.id) {
      return [columnDef.id];
    }

    return [];
  },
);

export const USER_LIST_COLUMN_ORDER = [...USER_LIST_COLUMN_ID_LIST];

export const USER_LIST_COLUMN_VISIBILITY: VisibilityState =
  USER_LIST_COLUMN_ID_LIST.reduce((columnVisibility, columnId) => {
    return { ...columnVisibility, [columnId]: true };
  }, {} as VisibilityState);

// /////////////////////////////////////////////////////////////////////////////
// OPTIONS
// /////////////////////////////////////////////////////////////////////////////

export interface UserListOptionsModel {
  columnVisibility: VisibilityState;
  columnOrder: ColumnOrderState;
}

export const USER_LIST_DEFAULT_OPTIONS: UserListOptionsModel = {
  columnVisibility: USER_LIST_COLUMN_VISIBILITY,
  columnOrder: USER_LIST_COLUMN_ORDER,
};

export const USER_LIST_COLUMN_LABEL_I18N_KEY: Record<string, string> = {
  firstName: 'delmonicos-shared-sdk.outputs-user.first-name',
  lastName: 'delmonicos-shared-sdk.outputs-user.last-name',
  mobileNumber: 'delmonicos-shared-sdk.outputs-user.mobile-number',
  emailAddress: 'delmonicos-shared-sdk.outputs-user.email-address',
  customer: 'delmonicos-shared-sdk.outputs-user.customer',
  roles: 'delmonicos-shared-sdk.outputs-user.role',
  lastConnection: 'delmonicos-shared-sdk.outputs-user.last-connection',
};
